import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({ baseURL: BASE, withCredentials: true });

export const wsUrl = () =>
  `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws`;

export const api = {
  // auth
  login: (email, password) => client.post("/auth/login", { email, password }),
  forgotPassword: (body) => client.post("/auth/forgot-password", body),

  resetPassword: (body) => client.post("/auth/reset-password", body),
  me: () => client.get("/auth/me"),
  logout: () => client.post("/auth/logout"),
  // org
  restaurants: () => client.get("/restaurants"),
  branches: () => client.get("/branches"),
  createBranch: (body) => client.post("/branches", body),
  updateBranch: (id, body) => client.patch(`/branches/${id}`, body),
  // menu
  categories: () => client.get("/categories"),
  createCategory: (body) => client.post("/categories", body),
  menuItems: (params) => client.get("/menu-items", { params }),
  uploadImage: async (file) => {
    const fd = new FormData();
    fd.append("file", file);

    const response = await client.post("/upload/menu-image", fd);

    if (response.data?.url?.startsWith("/")) {
      response.data.url = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
    }

    return response;
  },
  createMenuItem: (body) => client.post("/menu-items", body),
  updateMenuItem: (id, body) => client.put(`/menu-items/${id}`, body),
  deleteMenuItem: (id) => client.delete(`/menu-items/${id}`),
  toggleAvailability: (id, available) =>
    client.patch(`/menu-items/${id}/availability`, { available }),
  // tables
  tables: (branch_id) => client.get("/tables", { params: { branch_id } }),
  setTableStatus: (id, status) =>
    client.patch(`/tables/${id}/status`, { status }),
  regenerateQr: (id) => client.post(`/tables/${id}/qr`),
  createTable: (body) => client.post("/tables", body),
  updateTable: (id, body) => client.patch(`/tables/${id}`, body),
  deleteTable: (id) => client.delete(`/tables/${id}`),
  // orders
  createOrder: (body) => client.post("/orders", body),
  orders: (params) => client.get("/orders", { params }),
  order: (id) => client.get(`/orders/${id}`),
  addOrderItems: (id, items) => client.post(`/orders/${id}/items`, { items }),
  setOrderStatus: (id, status) =>
    client.patch(`/orders/${id}/status`, { status }),
  payOrder: (id, payments) =>
    client.post(`/orders/${id}/payment`, { payments }),

  // Existing order Razorpay flow
  createRazorpayOrder: (id) => client.post(`/orders/${id}/razorpay/order`),
  verifyRazorpayPayment: (id, body) =>
    client.post(`/orders/${id}/razorpay/verify`, body),

  // Payment-first Razorpay flow
  createRazorpayPaymentOrder: (body) =>
    client.post("/payments/razorpay/order", body),

  verifyRazorpayPaymentFirst: (body) =>
    client.post("/payments/razorpay/verify", body),

  cancelOrder: (id) => client.post(`/orders/${id}/cancel`),
  cancelOrder: (id) => client.post(`/orders/${id}/cancel`),
  approveOrder: (id) => client.post(`/orders/${id}/approve`),
  rejectOrder: (id, reason) => client.post(`/orders/${id}/reject`, { reason }),
  requestBill: (id) => client.post(`/orders/${id}/request-bill`),
  generateBill: (id) => client.post(`/orders/${id}/generate-bill`),
  audit: (order_id) => client.get("/audit", { params: { order_id } }),
  // kot / kitchen
  kots: (params) => client.get("/kots", { params }),
  setKotStatus: (id, status) => client.patch(`/kots/${id}/status`, { status }),
  // inventory
  inventory: (branch_id) => client.get("/inventory", { params: { branch_id } }),
  inventoryTransactions: (branch_id, date) =>
    client.get("/inventory/transactions", {
      params: {
        branch_id,
        ...(date ? { date } : {}),
      },
    }),
  createInventory: (body) => client.post("/inventory", body),
  updateInventory: (id, body) => client.patch(`/inventory/${id}`, body),
  deleteInventory: (id) => client.delete(`/inventory/${id}`),
  inventoryTxn: (id, body) => client.post(`/inventory/${id}/transaction`, body),
  customers: (params = {}) =>
    client.get("/customers", {
      params,
    }),

  createCustomer: (body) => client.post("/customers", body),

  customer: (id) => client.get(`/customers/${id}`),
  // others
  staff: (branch_id) => client.get("/staff", { params: { branch_id } }),
  createStaff: (body) => client.post("/staff", body),
  updateStaff: (id, body) => client.patch(`/staff/${id}`, body),
  deleteStaff: (id) => client.delete(`/staff/${id}`),
  resetStaffPassword: (id, password) =>
    client.patch(`/staff/${id}/password`, { password }),

  attendance: (params) => client.get("/attendance", { params }),
  markAttendance: (body) => client.post("/attendance", body),

  expenses: (branch_id) => client.get("/expenses", { params: { branch_id } }),
  createExpense: (body) => client.post("/expenses", body),
  reservations: (branch_id) =>
    client.get("/reservations", { params: { branch_id } }),
  createReservation: (body) => client.post("/reservations", body),
  setReservationStatus: (id, status) =>
    client.patch(`/reservations/${id}/status`, { status }),
  notifications: (branch_id) =>
    client.get("/notifications", { params: { branch_id } }),
  // analytics
  dashboard: (branch_id) => client.get("/dashboard", { params: { branch_id } }),
  reports: (branch_id, params = {}) =>
    client.get("/reports/summary", {
      params: {
        branch_id,
        ...params,
      },
    }),

  adminOverview: () => client.get("/admin/overview"),

  // Sales Partner Management
  adminSalesPartners: () => client.get("/admin/sales-partners"),

  adminSalesPartnerRestaurants: (id) =>
    client.get(`/admin/sales-partners/${id}/restaurants`),

  createAdminSalesPartner: (body) => client.post("/admin/sales-partners", body),

  updateAdminSalesPartner: (id, body) =>
    client.patch(`/admin/sales-partners/${id}`, body),

  deleteAdminSalesPartner: (id) => client.delete(`/admin/sales-partners/${id}`),

  updateAdminRestaurant: (id, body) =>
    client.patch(`/admin/restaurants/${id}`, body),

  // Super Admin: Create subscription renewal link
  createAdminRestaurantRenewal: (restaurantId) =>
    client.post(
      `/admin/restaurants/${restaurantId}/subscription/renewal`
    ),

  createSubscription: (restaurantId) =>
    client.post(`/admin/restaurants/${restaurantId}/subscription/create`),

  verifyTrialSubscription: (restaurantId, body) =>
    client.post(`/admin/restaurants/${restaurantId}/subscription/verify`, body),
  createAdminRestaurant: (body) => client.post("/admin/restaurants", body),
  settings: () => client.get("/settings"),
  rolePermissions: () => client.get("/settings"),
  updateRolePermissions: (role_permissions) =>
    client.patch("/settings/role-permissions", { role_permissions }),
  updateSettings: (body) => client.patch("/settings", body),
  plans: () => client.get("/plans"),
  publicPlans: () => client.get("/public/plans"),
  // public signup (no auth)
  publicSignup: (body) => client.post("/public/signup", body),
  createSignupPayment: (restaurantId) =>
    client.post(`/public/signup/${restaurantId}/payment/create`),
  verifySignupPayment: (restaurantId, body) =>
    client.post(`/public/signup/${restaurantId}/payment/verify`, body),
  // order additions / approvals
  orderAdditions: (params = {}) =>
    client.get("/order-additions", {
      params,
    }),

  approveOrderAddition: (orderId, additionId) =>
    client.post(`/orders/${orderId}/additions/${additionId}/approve`),

  rejectOrderAddition: (orderId, additionId, reason) =>
    client.post(`/orders/${orderId}/additions/${additionId}/reject`, {
      reason,
    }),
  // public
  // ---------------------------------------------------------
  // public QR / customer ordering
  // ---------------------------------------------------------

  publicMenu: (branch_id, table_id, qr_token) =>
    client.get("/public/menu", {
      params: {
        branch_id,
        table: table_id,
        qr_token,
      },
    }),

  // Customer creates first order from QR
  publicOrder: (body) => client.post("/public/order", body),

  // Customer checks current order
  publicOrderStatus: (order_id, branch_id, table_id, qr_token) =>
    client.get(`/public/order/${order_id}`, {
      params: {
        branch_id,
        table_id,
        qr_token,
      },
    }),

  // Customer adds more items to existing order
  publicAddOrderItems: (order_id, body) =>
    client.post(`/public/order/${order_id}/items`, body),

  // Customer requests bill
  publicRequestBill: (order_id, branch_id, table_id, qr_token) =>
    client.post(`/public/order/${order_id}/request-bill`, null, {
      params: {
        branch_id,
        table_id,
        qr_token,
      },
    }),
  // ---------------------------------------------------------
  // support tickets
  // ---------------------------------------------------------

  createSupportTicket: (body) => client.post("/support/tickets", body),

  mySupportTickets: () => client.get("/support/tickets/my"),

  adminSupportTickets: (params = {}) =>
    client.get("/admin/support/tickets", { params }),

  adminSupportTicket: (ticketId) =>
    client.get(`/admin/support/tickets/${ticketId}`),

  updateAdminSupportTicket: (ticketId, body) =>
    client.patch(`/admin/support/tickets/${ticketId}`, body),
};

export default client;
